import React from "react";
import { Input, ToggleSwitch } from "../../../../components";
import { useTranslations } from "../../../../contexts";
import { DesignerSectionHeading } from "./DesignerSectionHeading";
import type { SidebarFormProps } from "./SidebarForm";

type FaqItem = NonNullable<
  NonNullable<SidebarFormProps["form"]["faq"]>["items"]
>[number];

export const SidebarFormFaq: React.FC<SidebarFormProps> = (props) => {
  const i18n = useTranslations();
  const faq = props.form.faq;
  const items = faq?.items || [];

  const updateItem = (
    item: FaqItem,
    index: number,
    field: "question" | "answer",
    value: string
  ) => {
    const updatedItem = { ...item, [field]: value };
    const updatedItems =
      !updatedItem.question?.trim() && !updatedItem.answer?.trim()
        ? items.filter((_, itemIndex) => itemIndex !== index)
        : index < items.length
          ? items.map((existingItem, itemIndex) =>
              itemIndex === index ? updatedItem : existingItem
            )
          : [...items, updatedItem];

    props.setForm({
      ...props.form,
      faq: {
        ...faq,
        items: updatedItems,
      },
    });
  };

  return (
    <div className="border-b p-6" data-preview-section="faq">
      <div className="flex items-start">
        <DesignerSectionHeading
          className="grow pb-4"
          title={i18n.FAQ}
          helpLabel={`${i18n.designerHelpFor} ${i18n.FAQ}`}
          helpText={i18n.designerFaqHelp}
        />
        <ToggleSwitch
          aria-label={`${i18n.designerShowSection}: ${i18n.FAQ}`}
          checked={faq?.is_enabled}
          onChange={() =>
            props.setForm({
              ...props.form,
              faq: {
                ...faq,
                is_enabled: !faq?.is_enabled,
              },
            })
          }
        />
      </div>
      {faq?.is_enabled && (
        <div>
          <div className="pb-2">
            <Input
              variant="outlined"
              placeholder={i18n.title}
              example={i18n.designerFaqTitlePlaceholder}
              help={{
                label: `${i18n.designerHelpFor} ${i18n.title}`,
                content: i18n.designerFaqTitleHelp,
              }}
              value={faq.title || ""}
              onChange={(event) =>
                props.setForm({
                  ...props.form,
                  faq: {
                    ...faq,
                    title: event.target.value,
                  },
                })
              }
            />
          </div>
          {[...items, { id: crypto.randomUUID() }].map((item, index) => (
            <div className="pt-2" key={item.id}>
              <Input
                className="mb-2"
                variant="outlined"
                placeholder={i18n.Question}
                example={i18n.designerFaqQuestionPlaceholder}
                help={{
                  label: `${i18n.designerHelpFor} ${i18n.Question}`,
                  content: i18n.designerFaqQuestionHelp,
                }}
                value={item.question || ""}
                onChange={(event) =>
                  updateItem(item, index, "question", event.target.value)
                }
              />
              <Input
                variant="outlined"
                placeholder={i18n.Answer}
                example={i18n.designerFaqAnswerPlaceholder}
                asTextarea
                help={{
                  label: `${i18n.designerHelpFor} ${i18n.Answer}`,
                  content: i18n.designerFaqAnswerHelp,
                }}
                value={item.answer || ""}
                onChange={(event) =>
                  updateItem(item, index, "answer", event.target.value)
                }
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
